import { stepMonitoring } from './monitoring';
import { epochDayOf } from './calendar';
import { stepTaggedOthers } from './tags';
import type { GameEvent, World } from './types';
import { Rng } from './rng';
import { stepEnvironment } from './systems/environment';
import { stepClimate, stepHeatwaves } from './systems/climate';
import { stepHeatStress } from './systems/heatStress';
import { stepCoral } from './systems/coral';
import { stepPlankton } from './systems/plankton';
import { stepAnimals } from './systems/fish';
import { stepTurtles } from './systems/turtles';
import { stepWhales } from './systems/whales';
import { stepSeals } from './systems/seals';
import { stepSharks } from './systems/sharks';
import { stepSlope } from './systems/slope';
import { stepMigrants } from './systems/migrants';
import { stepOperations } from './operations';
import { stepCampaign } from './campaign';

/**
 * The tick: one day, for every ocean.
 *
 * The order is fixed and deliberate, and it is Sable Reserve's:
 *
 *   1. environment     climate, light, ice
 *   2. production      plankton, coral, kelp, seagrass
 *   3. animals         movement and migration, feeding, predation, demography
 *   4. estate          stations, vessels, staff, projects
 *   5. threats         fishing, bycatch, illegal fishing, strikes, pollution
 *   6. money, events   the books, incidents, the log
 *
 * The environment sets the conditions, production grows, animals go where the
 * food is and eat it, predators eat the animals, births and deaths resolve on
 * the condition that resulted, and only then does the business run. Each
 * system steps every region and every sanctuary, viewed or not.
 *
 * Phase 1 has the clock, the sun, the water and its year to year
 * climate, and Phase 2 begins with the coral (M47, `docs/ROADMAP.md`), Phase
 * 3 with the first reef fish (M74). The streams are opened and written back
 * so that each system that draws finds them where this says they are: the
 * climate draws on its own (M24); the fish on each island's own stream of the
 * day (M80, `stockStream`), carried by no field, and the honu, the
 * humpbacks, the monk seals, the North Atlantic right whales, the
 * Pacific blue marlin, the western Pacific leatherbacks, the eastern
 * North Pacific gray whales and the Deep 7 each on its own (M93, M101, M103,
 * M107, M108, M111, M112, M192); the coral and the deepwater shrimp (M193) draw nothing, and nothing on the
 * ecology's carried stream since M80.
 */

export interface TickReport {
  day: number;
  events: GameEvent[];
}

export function tick(world: World): TickReport {
  if (world.gameOver) return { day: world.day, events: [] };

  const climate = Rng.fromState(world.climateRngState);
  world.day++;
  const events: GameEvent[] = [];

  // 1. environment
  stepEnvironmentStage(world, climate, events);
  // 2. production
  stepProductionStage(world, events);
  // 3. animals
  stepAnimalStage(world);
  // 4. the business (M238): work finished on the day's world, the grant, the month's accounts.
  stepOperations(world, events);
  // 5. the campaign (M239): its objectives read from the day's world.
  stepCampaign(world, events);

  world.climateRngState = climate.seed;
  for (const e of events) world.log.push(e);
  return { day: world.day, events };
}

/**
 * Step 1, the environment: the year's climate and each place's departure (the
 * climate stream's only draws), the day's water and sun, then the heatwaves
 * and the reefs' heat stress (M46). One function, which the tick and the
 * save's replay of the climate both run, so the replay cannot fall out of
 * step with the tick (review M25).
 */
export function stepEnvironmentStage(world: World, climate: Rng, events: GameEvent[]): void {
  stepClimate(world, climate, events);
  stepEnvironment(world);
  stepHeatwaves(world, events);
  stepHeatStress(world, events);
}

/**
 * Step 3, the animals: the reef fish (M74), then the honu (M93), then the
 * humpbacks (M101), then the monk seals (M103), then the deep slope, the
 * Deep 7 bottomfish (M192) and the deepwater shrimp (M193), then the populations of no
 * sanctuary, the North Atlantic right whales (M107), the Pacific blue marlin
 * (M108), the western Pacific leatherbacks (M111) and the eastern North
 * Pacific gray whales (M112), each on its own
 * streams of the day. The tick and the save's replay both run it.
 */
export function stepAnimalStage(world: World): void {
  stepAnimals(world);
  stepTurtles(world);
  stepWhales(world);
  stepSeals(world);
  stepSlope(world);
  stepSharks(world);
  // The tagged honu, seals and humpbacks (M148), each on its own stream after its population; the reef's tagged in `stepAnimals`.
  if (world.tags.animals.length > 0) stepTaggedOthers(world, epochDayOf(world));
  stepMigrants(world);
  // The day's surveys, on the day's animals (M160).
  stepMonitoring(world);
}

/**
 * Step 2, production: the plankton (M52), in the water and light step 1
 * left, then the coral (M47), under its heat. The tick and the save's replay
 * both run it.
 */
export function stepProductionStage(world: World, events: GameEvent[]): void {
  stepPlankton(world);
  stepCoral(world, events);
}

export function tickMany(world: World, days: number): void {
  for (let i = 0; i < days; i++) tick(world);
}
